import React, { useMemo, useState } from 'react';
import { History, Star, TrendingUp, UserCheck, X } from 'lucide-react';
import { useUsuarios } from '../../usuarios/hooks';
import {
  useAtualizarCliente,
  useClassificacoes,
  useCliente,
  useHistoricoResponsavel,
  useTrocarResponsavel,
} from '../hooks';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import type { User } from '../../../shared/lib/api';
import type { CommercialPotential } from '../api';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Textarea } from '../../../shared/components/Textarea';

interface Props {
  clientId: string;
}

const POTENTIAL_LABEL: Record<CommercialPotential, string> = {
  baixo: 'Baixo',
  medio: 'Médio',
  alto: 'Alto',
};

const POTENTIAL_BADGE: Record<CommercialPotential, string> = {
  baixo: 'bg-gray-100 text-gray-700',
  medio: 'bg-blue-100 text-blue-800',
  alto: 'bg-green-100 text-green-800',
};

/**
 * Perfil comercial do cliente — hoje só o responsável comercial
 * (SPEC 02 §1 — RN-COM-043, RN-COM-044). As demais dimensões
 * (classificação parametrizável, potencial comercial) vêm na Fase B.
 *
 * Regras aplicadas na UI:
 *  - `motivo` é obrigatório antes do submit — bloqueia o botão
 *    "Salvar troca" enquanto vazio (RN-COM-044).
 *  - Trocar o responsável pelo mesmo usuário é bloqueado.
 *  - Histórico de trocas listado abaixo em ordem cronológica DESC.
 */
export const ClientePerfilComercial: React.FC<Props> = ({ clientId }) => {
  const { data: cliente, isLoading: loadingCliente } = useCliente(clientId);
  const { data: usuariosData, isLoading: loadingUsuarios } = useUsuarios({ limit: 500 });
  const { data: historico } = useHistoricoResponsavel(clientId);
  const { data: classificacoesData, isLoading: loadingCls } = useClassificacoes();
  const trocar = useTrocarResponsavel(clientId);
  const atualizarCliente = useAtualizarCliente();
  const [modalOpen, setModalOpen] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  const usuarios: User[] = usuariosData?.data ?? [];
  const responsavelAtual = useMemo(
    () => usuarios.find((u) => u.id === cliente?.responsibleUserId) ?? null,
    [usuarios, cliente?.responsibleUserId],
  );
  const classificacoes = classificacoesData?.data ?? [];
  const classificacaoAtual = useMemo(
    () => classificacoes.find((c) => c.id === cliente?.classificationId) ?? null,
    [classificacoes, cliente?.classificationId],
  );

  const showFeedback = (kind: 'ok' | 'err', msg: string) => {
    setFeedback({ kind, msg });
    setTimeout(() => setFeedback(null), 4000);
  };

  const handleClassificacaoChange = async (classificationId: string) => {
    try {
      await atualizarCliente.mutateAsync({
        id: clientId,
        data: { classificationId: classificationId || null },
      });
      showFeedback('ok', 'Classificação atualizada.');
    } catch (err) {
      showFeedback('err', getFriendlyErrorMessage(err, 'Erro ao atualizar classificação.'));
    }
  };

  const handlePotencialChange = async (value: CommercialPotential | '') => {
    try {
      await atualizarCliente.mutateAsync({
        id: clientId,
        data: { commercialPotential: value === '' ? null : value },
      });
      showFeedback('ok', 'Potencial comercial atualizado.');
    } catch (err) {
      showFeedback('err', getFriendlyErrorMessage(err, 'Erro ao atualizar potencial.'));
    }
  };

  return (
    <div className="space-y-4" data-testid="cliente-perfil-comercial" data-pending-anchor="perfilComercial">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <UserCheck size={18} /> Perfil Comercial
        </h3>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          disabled={loadingUsuarios || loadingCliente}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          data-testid="btn-trocar-responsavel"
        >
          <UserCheck size={16} /> {cliente?.responsibleUserId ? 'Trocar responsável' : 'Definir responsável'}
        </button>
      </div>

      {feedback && (
        <div
          className={`rounded-md border p-3 text-sm ${
            feedback.kind === 'ok'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
          data-testid="perfil-feedback"
        >
          {feedback.msg}
        </div>
      )}

      <div className="rounded-md border border-gray-200 bg-white p-4">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Responsável comercial principal</div>
        {loadingCliente || loadingUsuarios ? (
          <div className="text-sm text-gray-500">Carregando…</div>
        ) : responsavelAtual ? (
          <div className="text-sm text-gray-900">
            <div className="font-medium">{responsavelAtual.name}</div>
            <div className="text-xs text-gray-500">{responsavelAtual.email} · {responsavelAtual.role}</div>
          </div>
        ) : (
          <div className="rounded border border-dashed border-yellow-300 bg-yellow-50 p-2 text-sm text-yellow-800">
            Sem responsável definido. A ativação do cliente exige responsável comercial (RN-COM-043).
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-md border border-gray-200 bg-white p-4">
          <div className="mb-1 flex items-center gap-1 text-xs uppercase tracking-wide text-gray-500">
            <Star size={12} /> Classificação estratégica
          </div>
          <AutocompleteSelect
            label="Classificação"
            options={[
              { value: '', label: '— Não classificado —' },
              ...classificacoes.map((c) => ({ value: c.id, label: c.name })),
              ...(classificacaoAtual === null &&
              cliente?.classificationId &&
              !classificacoes.some((c) => c.id === cliente.classificationId)
                ? [{ value: cliente.classificationId, label: '(classificação inativa)' }]
                : []),
            ]}
            value={cliente?.classificationId ?? ''}
            onChange={handleClassificacaoChange}
            disabled={loadingCls || atualizarCliente.isPending}
          />
          <span className="mt-1 block text-xs text-gray-500">
            Valores administrados por perfil admin (RN-COM-045).
          </span>
        </div>

        <div className="rounded-md border border-gray-200 bg-white p-4">
          <div className="mb-1 flex items-center gap-1 text-xs uppercase tracking-wide text-gray-500">
            <TrendingUp size={12} /> Potencial comercial
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <AutocompleteSelect
                label="Potencial"
                options={[
                  { value: '', label: '— Não estimado —' },
                  { value: 'baixo', label: 'Baixo' },
                  { value: 'medio', label: 'Médio' },
                  { value: 'alto', label: 'Alto' },
                ]}
                value={cliente?.commercialPotential ?? ''}
                onChange={(v) => handlePotencialChange(v as CommercialPotential | '')}
                disabled={atualizarCliente.isPending}
              />
            </div>
            {cliente?.commercialPotential && (
              <span
                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${POTENTIAL_BADGE[cliente.commercialPotential]}`}
              >
                {POTENTIAL_LABEL[cliente.commercialPotential]}
              </span>
            )}
          </div>
          <span className="mt-1 block text-xs text-gray-500">
            Manual ou automático via scoring (porte, segmento, contratos, receita) — RN-COM-046.
          </span>
        </div>
      </div>

      <div className="rounded-md border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
          <History size={14} /> Histórico de trocas
        </div>
        {!historico || historico.data.length === 0 ? (
          <div className="text-xs text-gray-500">Nenhuma troca registrada.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {historico.data.map((h) => (
              <li key={h.id} className="py-2 text-xs text-gray-700" data-testid={`resp-hist-${h.id}`}>
                <div className="text-gray-500">
                  {new Date(h.createdAt).toLocaleString('pt-BR')}
                  {h.changedByName ? ` · por ${h.changedByName}` : ''}
                </div>
                <div>
                  <span className="text-gray-500">De:</span>{' '}
                  <strong>{h.previousUserName ?? '—'}</strong>
                  {' → '}
                  <span className="text-gray-500">Para:</span>{' '}
                  <strong>{h.newUserName ?? '—'}</strong>
                </div>
                <div className="text-gray-500">Motivo: <em>{h.reason}</em></div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {modalOpen && (
        <TrocarResponsavelModal
          usuarios={usuarios.filter((u) => u.id !== cliente?.responsibleUserId)}
          responsavelAtualId={cliente?.responsibleUserId ?? null}
          submitting={trocar.isPending}
          onCancel={() => setModalOpen(false)}
          onSubmit={async ({ newUserId, reason }) => {
            try {
              await trocar.mutateAsync({ newUserId, reason });
              showFeedback('ok', 'Responsável comercial atualizado.');
              setModalOpen(false);
            } catch (err) {
              showFeedback('err', getFriendlyErrorMessage(err, 'Erro ao trocar responsável.'));
            }
          }}
        />
      )}
    </div>
  );
};

interface TrocarResponsavelModalProps {
  usuarios: User[];
  responsavelAtualId: string | null;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (payload: { newUserId: string; reason: string }) => Promise<void>;
}

const TrocarResponsavelModal: React.FC<TrocarResponsavelModalProps> = ({
  usuarios,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const [newUserId, setNewUserId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const podeSubmeter = !!newUserId && reason.trim().length >= 3;

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    setError(null);
    if (!newUserId) {
      setError('Selecione o novo responsável.');
      return;
    }
    if (reason.trim().length < 3) {
      setError('Motivo é obrigatório (mínimo 3 caracteres).');
      return;
    }
    await onSubmit({ newUserId, reason: reason.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h3 className="text-xl font-bold text-gray-900">Trocar responsável comercial</h3>
          <button type="button" onClick={onCancel} aria-label="Fechar" className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 p-6" data-testid="responsavel-form">
          <AutocompleteSelect
            label="Novo responsável *"
            options={[
              { value: '', label: 'Selecione…' },
              ...usuarios.map((u) => ({ value: u.id, label: `${u.name} — ${u.email}` })),
            ]}
            value={newUserId}
            onChange={setNewUserId}
          />

          <div>
            <Textarea
              label="Motivo *"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              data-testid="responsavel-motivo"
            />
            <span className="mt-1 block text-xs text-gray-500">
              O motivo é obrigatório e fica registrado no histórico do cliente (RN-COM-044).
            </span>
          </div>

          {error && (
            <div
              className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700"
              data-testid="responsavel-form-error"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || !podeSubmeter}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              data-testid="responsavel-btn-salvar"
            >
              {submitting ? 'Salvando…' : 'Salvar troca'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
